import {
  HHButton,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import { useCallback, useState } from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  CvsFeeScheduleType,
  PaymentType,
  useDeleteCvsFeeScheduleMutation,
  useUpdateCvsFeeScheduleMutation,
} from '../../types';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import { CvsFeeScheduleForm } from './cvs-fee-schedule-form/form';
import {
  chargeToDisplayValue,
  CvsFeeScheduleFormValues,
  daysSupplyToDisplayValue,
  displayValueToCharge,
  displayValueToDaysSupply,
} from './cvs-fee-schedule-form/validation-schema';
import { useCvsFeeScheduleColumns } from './use-cvs-fee-schedule-columns';
import { useCvsFeeScheduleQueries } from './use-cvs-fee-schedule-queries';

export const CVS_FEE_SCHEDULE_GRID_TEST_ID = 'cvs-fee-schedule-grid';

const DEFAULT_PAGE_SIZE = 50;

interface CvsFeeScheduleGridProps {
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

export const CvsFeeScheduleGrid = ({
  paymentTypes,
  paymentTypesLoading,
}: CvsFeeScheduleGridProps): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { rows, loading, hasError } = useCvsFeeScheduleQueries();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setDialogContent, closeDialog } = useDynamicDialogContext();
  const { setSnackbarState } = useSnackbarContext();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [updateCvsFeeSchedule] = useUpdateCvsFeeScheduleMutation();
  const [deleteCvsFeeSchedule] = useDeleteCvsFeeScheduleMutation();

  const handleEdit = useCallback(
    (row: CvsFeeScheduleType): void => {
      // CVS stores charge as zero-padded cents (e.g. "00015000" = $150.00)
      // and daysSupply as a zero-padded string (e.g. "030" = 30 days).
      // Convert to human-readable values for the edit form.
      const initialValues: CvsFeeScheduleFormValues = {
        paymentType: row.paymentType ?? '',
        price: row.price,
        upc: row.upc,
        charge: String(chargeToDisplayValue(row.charge)),
        daysSupply: daysSupplyToDisplayValue(row.daysSupply),
        description: row.description ?? '',
      };

      const handleUpdate = async (
        values: CvsFeeScheduleFormValues,
      ): Promise<void> => {
        try {
          await updateCvsFeeSchedule({
            variables: {
              id: row.id,
              // Convert form values back to CVS storage formats before sending
              cvsFeeScheduleInput: {
                paymentType: values.paymentType || null,
                price: values.price as number,
                upc: values.upc,
                charge: displayValueToCharge(values.charge),
                daysSupply: displayValueToDaysSupply(values.daysSupply),
                description: values.description || null,
              },
            },
            refetchQueries: ['getCvsFeeSchedules'],
          });
          setSnackbarState(
            'CVS fee schedule updated successfully',
            'success',
            3000,
          );
          onClose();
        } catch {
          setSnackbarState('Error updating CVS fee schedule', 'error', 3000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <CvsFeeScheduleForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            paymentTypes={paymentTypes}
            paymentTypesLoading={paymentTypesLoading}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit CVS Fee Schedule</HHTypography>,
        undefined,
        'temporary',
      );
    },
    [
      setDrawerContent,
      updateCvsFeeSchedule,
      paymentTypes,
      paymentTypesLoading,
      setSnackbarState,
      onClose,
    ],
  );

  const handleDelete = useCallback(
    (row: CvsFeeScheduleType): void => {
      const executeDelete = async (): Promise<void> => {
        closeDialog();
        try {
          await deleteCvsFeeSchedule({
            variables: { id: row.id },
            refetchQueries: ['getCvsFeeSchedules'],
          });
          setSnackbarState(
            'CVS fee schedule deleted successfully',
            'success',
            3000,
          );
        } catch {
          setSnackbarState('Error deleting CVS fee schedule', 'error', 3000);
        }
      };

      setDialogContent({
        title: (
          <HHTypography hhVariant="section-title">
            Delete CVS Fee Schedule
          </HHTypography>
        ),
        content: (
          <HHStack spacing={2}>
            <HHTypography hhVariant="body1">
              Are you sure you want to delete this CVS fee schedule (
              {row.paymentType ?? '—'} — {row.upc})?
            </HHTypography>
          </HHStack>
        ),
        footer: (
          <HHStack direction="row" spacing={2} padding={1}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              onClick={closeDialog}
              data-testid="cvs-fee-schedule-delete-cancel-button"
            >
              Cancel
            </HHButton>
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color="error"
              onClick={(): void => {
                void executeDelete();
              }}
              data-testid="cvs-fee-schedule-delete-confirm-button"
            >
              Delete
            </HHButton>
          </HHStack>
        ),
        width: 'sm',
        fullWidth: true,
      });
    },
    [setDialogContent, closeDialog, deleteCvsFeeSchedule, setSnackbarState],
  );

  const columns = useCvsFeeScheduleColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    canWrite,
  });

  if (hasError) {
    return (
      <HHError
        body="Error fetching CVS fee schedules"
        hhVariant="variant-bypass"
      />
    );
  }

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
    >
      <DataGridPro
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row): number => row.id}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="client"
        initialState={{
          sorting: { sortModel: [{ field: 'upc', sort: 'asc' }] },
        }}
        sortingMode="client"
        filterMode="client"
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        pinnedColumns={{ right: ['actions'] }}
        aria-label="CVS fee schedules table"
        data-testid={CVS_FEE_SCHEDULE_GRID_TEST_ID}
        {...(process.env.NODE_ENV === 'test' && {
          disableVirtualization: true,
        })}
        slots={{
          noResultsOverlay: NoRowsOverlay,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          noResultsOverlay: { error: hasError } as Record<string, boolean>,
          noRowsOverlay: { error: hasError } as Record<string, boolean>,
        }}
      />
    </HHStack>
  );
};
